import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { io } from 'socket.io-client';
import { MessageCircle, Users, Shield, Sword, Send, Crown, ChevronLeft, DollarSign, AlertTriangle, Hash, MapPin, Star } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Socket.IO singleton
let socket = null;
const getSocket = () => {
  if (!socket) {
    socket = io(API, {
      path: '/api/socketio/',
      transports: ['polling', 'websocket'],
      autoConnect: true,
    });
  }
  return socket;
};

// =============================================
// CHAT TAB
// =============================================
const ChatPanel = ({ gameId, username, crewId, activeChannel, messages, onSend }) => {
  const [text, setText] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!text.trim()) return;
    onSend(text.trim());
    setText('');
  };

  return (
    <div className="flex flex-col h-[350px]">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-1 p-2 scrollbar-hide">
        {messages.length === 0 && (
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-xs text-center py-8">No messages yet.</p>
        )}
        {messages.map((m, i) => {
          const isMe = m.game_id === gameId;
          return (
            <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] px-2.5 py-1.5 rounded-lg ${
                isMe ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/20' : 'bg-[#111820] border border-[#1A2535]'
              }`}>
                {!isMe && (
                  <p className="font-['Outfit'] text-[7px] text-[#C9A84C]/50 font-semibold">{m.username || 'Unknown'}</p>
                )}
                <p className="font-['Outfit'] text-xs text-[#E8E0D0]/80 break-words">{m.text}</p>
                <p className="font-['Outfit'] text-[6px] text-[#E8E0D0]/15 text-right mt-0.5">
                  {m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 p-2 border-t border-[#1A2535]">
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder={activeChannel === 'global' ? 'Say something...' : activeChannel === 'crew' ? 'Crew chat...' : 'Direct message...'}
          className="flex-1 px-3 py-2 bg-[#0a0c10] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs focus:border-[#C9A84C]/30 outline-none"
          data-testid="chat-input"
        />
        <button onClick={handleSend}
          className="p-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg text-[#C9A84C] hover:bg-[#C9A84C]/20"
          data-testid="chat-send">
          <Send size={14} />
        </button>
      </div>
    </div>
  );
};

// =============================================
// CREW PANEL
// =============================================
const CrewPanel = ({ gameId, crew, onRefresh }) => {
  const [createName, setCreateName] = useState('');
  const [createTag, setCreateTag] = useState('');
  const [creating, setCreating] = useState(false);
  const [joinId, setJoinId] = useState('');
  const [topCrews, setTopCrews] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/crew/list/top`).then(r => r.json()).then(d => setTopCrews(d.crews || [])).catch(() => {});
  }, []);

  const createCrew = async () => {
    setCreating(true); setError(null);
    try {
      const res = await fetch(`${API}/api/crew/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, name: createName, tag: createTag }),
      });
      const data = await res.json();
      if (res.ok && data.success) { onRefresh(); }
      else setError(data.detail);
    } catch (e) { setError('Network error'); }
    setCreating(false);
  };

  const joinCrew = async (crewId) => {
    setError(null);
    try {
      const res = await fetch(`${API}/api/crew/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, crew_id: crewId }),
      });
      const data = await res.json();
      if (res.ok && data.success) onRefresh();
      else setError(data.detail);
    } catch (e) { setError('Network error'); }
  };

  const leaveCrew = async () => {
    try {
      const res = await fetch(`${API}/api/crew/leave?game_id=${gameId}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.success) onRefresh();
      else setError(data.detail);
    } catch (e) { setError('Network error'); }
  };

  // If in a crew, show crew details
  if (crew) {
    const RANK_COLORS = { boss: '#FFD700', underboss: '#C9A84C', captain: '#4A7AB5', soldier: '#4ADE80', prospect: '#E8E0D0' };
    return (
      <div className="space-y-3 p-2">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">[{crew.tag}] {crew.name}</h4>
            <p className="font-['Outfit'] text-[9px] text-[#C9A84C]">Your rank: {crew.my_rank?.toUpperCase()}</p>
          </div>
          <div className="text-right">
            <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">Level {crew.level}</p>
            <p className="font-['Outfit'] text-[8px] text-[#4ADE80]">${crew.treasury?.toLocaleString()} treasury</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20">MEMBERS</p>
            <p className="font-['Outfit'] text-sm text-[#E8E0D0] font-bold">{crew.member_count}</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20">WAR WINS</p>
            <p className="font-['Outfit'] text-sm text-[#4ADE80] font-bold">{crew.war_wins}</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20">LOSSES</p>
            <p className="font-['Outfit'] text-sm text-[#8B1A1A] font-bold">{crew.war_losses}</p>
          </div>
        </div>

        {/* Members list */}
        <div>
          <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20 tracking-widest mb-1">MEMBERS</p>
          <div className="space-y-1 max-h-[150px] overflow-y-auto">
            {(crew.members || []).map((m, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-[#111820] rounded-lg">
                <span className="font-['Outfit'] text-xs text-[#E8E0D0]">{m.username}</span>
                <span className="font-['Outfit'] text-[8px] font-semibold" style={{ color: RANK_COLORS[m.rank] || '#E8E0D0' }}>
                  {m.rank?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <button onClick={leaveCrew}
          className="w-full py-1.5 bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 rounded text-[#8B1A1A] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#8B1A1A]/20">
          LEAVE CREW
        </button>
      </div>
    );
  }

  // Not in a crew — show create/join UI
  return (
    <div className="space-y-3 p-2">
      {error && <p className="font-['Outfit'] text-[#8B1A1A] text-xs">{error}</p>}

      {/* Create */}
      <div className="space-y-2">
        <p className="font-['Outfit'] text-[9px] text-[#C9A84C] tracking-widest">CREATE CREW (500 WATER CRYSTALS)</p>
        <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="Crew name"
          className="w-full px-3 py-2 bg-[#0a0c10] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs focus:border-[#C9A84C]/30 outline-none" />
        <div className="flex gap-2">
          <input type="text" value={createTag} onChange={e => setCreateTag(e.target.value.toUpperCase().slice(0, 5))} placeholder="TAG (2-5)"
            className="w-24 px-3 py-2 bg-[#0a0c10] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs focus:border-[#C9A84C]/30 outline-none" />
          <button onClick={createCrew} disabled={creating || !createName || !createTag}
            className="flex-1 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg text-[#C9A84C] font-['Outfit'] text-[9px] tracking-widest disabled:opacity-30">
            {creating ? '...' : 'CREATE'}
          </button>
        </div>
      </div>

      {/* Top crews to join */}
      {topCrews.length > 0 && (
        <div>
          <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/20 tracking-widest mb-1">JOIN A CREW</p>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {topCrews.map((c, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-2 bg-[#111820] rounded-lg border border-[#1A2535]">
                <div>
                  <span className="font-['Outfit'] text-xs text-[#E8E0D0] font-semibold">[{c.tag}] {c.name}</span>
                  <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">{c.member_count} members | W{c.war_wins}/L{c.war_losses}</p>
                </div>
                <button onClick={() => joinCrew(c.crew_id)}
                  className="px-2 py-1 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded text-[#C9A84C] font-['Outfit'] text-[8px] tracking-widest">
                  JOIN
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================
// WAR PANEL
// =============================================
const WarPanel = ({ gameId, crew }) => {
  const [wars, setWars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/war/active`).then(r => r.json()).then(d => setWars(d.wars || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-4">Loading...</p>;

  return (
    <div className="space-y-2 p-2">
      {wars.length === 0 ? (
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">No active wars. The streets are quiet... for now.</p>
      ) : wars.map((w, i) => {
        const isPreparing = w.status === 'preparing';
        return (
          <div key={i} className="bg-[#111820] border border-[#8B1A1A]/20 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className={`font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded ${isPreparing ? 'bg-[#C9A84C]/10 text-[#C9A84C]' : 'bg-[#8B1A1A]/10 text-[#8B1A1A]'}`}>
                {isPreparing ? 'PREPARING' : 'ACTIVE'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <p className="font-['Outfit'] text-xs text-[#C9A84C] font-bold">[{w.attacker_crew_tag}]</p>
                <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40">{w.attacker_crew_name}</p>
                <p className="font-['Outfit'] text-lg text-[#E8E0D0] font-bold">{w.attacker_score}</p>
              </div>
              <div className="px-3">
                <Sword size={16} className="text-[#8B1A1A]/40" />
              </div>
              <div className="text-center flex-1">
                <p className="font-['Outfit'] text-xs text-[#8B1A1A] font-bold">[{w.defender_crew_tag}]</p>
                <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40">{w.defender_crew_name}</p>
                <p className="font-['Outfit'] text-lg text-[#E8E0D0] font-bold">{w.defender_score}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =============================================
// BOUNTY BOARD
// =============================================
const BountyBoard = ({ gameId }) => {
  const [bounties, setBounties] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [postTerritory, setPostTerritory] = useState('');
  const [postReward, setPostReward] = useState('');
  const [postDesc, setPostDesc] = useState('');
  const [territorySearch, setTerritorySearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/chat/bounties`).then(r => r.json()).then(d => setBounties(d.bounties || [])).catch(() => {});
    // Fetch player's territories for dropdown
    fetch(`${API}/api/multiplayer/my-territories/${gameId}`).then(r => r.json()).then(d => {
      setTerritories(d.territories || []);
    }).catch(() => {});
  }, [gameId]);

  const filteredTerritories = territories.filter(t =>
    (t.kow_name || t.territory_id || '').toLowerCase().includes(territorySearch.toLowerCase()) ||
    (t.borough || '').toLowerCase().includes(territorySearch.toLowerCase())
  );

  const postBounty = async () => {
    const res = await fetch(`${API}/api/chat/bounty`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, territory_id: postTerritory, reward: parseInt(postReward), description: postDesc }),
    });
    if (res.ok) {
      setPostTerritory(''); setPostReward(''); setPostDesc('');
      const d = await fetch(`${API}/api/chat/bounties`).then(r => r.json());
      setBounties(d.bounties || []);
    }
  };

  return (
    <div className="space-y-3 p-2">
      <p className="font-['Outfit'] text-[9px] text-[#8B1A1A]/50 tracking-widest">ACTIVE BOUNTIES</p>
      <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
        {bounties.map((b, i) => (
          <div key={i} className="px-3 py-2 bg-[#111820] border border-[#8B1A1A]/15 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-['Outfit'] text-[#C9A84C] text-xs font-bold">{b.territory_id}</span>
              <span className="font-['Outfit'] text-[#4ADE80] text-xs font-bold">${b.reward?.toLocaleString()}</span>
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">{b.description}</p>
            <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[7px]">by {b.poster_name}</p>
          </div>
        ))}
        {bounties.length === 0 && <p className="font-['Outfit'] text-[#E8E0D0]/15 text-xs text-center py-4">No active bounties.</p>}
      </div>

      {/* Post bounty */}
      <div className="space-y-1.5">
        <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20 tracking-widest">POST BOUNTY</p>
        <div className="flex gap-1.5">
          <div className="flex-1 relative">
            <input type="text" value={postTerritory ? `${postTerritory}` : territorySearch}
              onChange={e => { setTerritorySearch(e.target.value); setPostTerritory(''); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              placeholder={territories.length ? "Search your territories..." : "No territories owned"}
              className="w-full px-2 py-1.5 bg-[#0a0c10] border border-[#1A2535] rounded text-[#E8E0D0] font-['Outfit'] text-[10px] outline-none"
              data-testid="bounty-territory-search" />
            {showDropdown && (
              <div className="absolute z-50 top-full mt-1 left-0 right-0 max-h-32 overflow-y-auto bg-[#0a0c10] border border-[#1A2535] rounded shadow-lg">
                {filteredTerritories.length === 0 ? (
                  <p className="px-2 py-2 font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">{territories.length ? 'No match' : 'Claim territories first'}</p>
                ) : filteredTerritories.slice(0, 15).map(t => (
                  <button key={t.territory_id} onClick={() => { setPostTerritory(t.territory_id); setTerritorySearch(t.kow_name || t.territory_id); setShowDropdown(false); }}
                    className="w-full px-2 py-1.5 text-left hover:bg-[#1A2535] flex items-center justify-between"
                    data-testid={`bounty-territory-${t.territory_id}`}>
                    <span className="font-['Outfit'] text-[#E8E0D0] text-[9px]">{t.kow_name || t.territory_id}</span>
                    <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">{t.borough}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input type="number" value={postReward} onChange={e => setPostReward(e.target.value)} placeholder="Reward $"
            className="w-20 px-2 py-1.5 bg-[#0a0c10] border border-[#1A2535] rounded text-[#E8E0D0] font-['Outfit'] text-[10px] outline-none" />
        </div>
        <div className="flex gap-1.5">
          <input type="text" value={postDesc} onChange={e => setPostDesc(e.target.value)} placeholder="Description..."
            className="flex-1 px-2 py-1.5 bg-[#0a0c10] border border-[#1A2535] rounded text-[#E8E0D0] font-['Outfit'] text-[10px] outline-none" />
          <button onClick={postBounty} disabled={!postTerritory || !postReward}
            className="px-3 py-1.5 bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 rounded text-[#8B1A1A] font-['Outfit'] text-[8px] tracking-widest disabled:opacity-30">
            POST
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================
// MAIN COMMUNICATION HUB
// =============================================
const CommunicationHub = ({ gameId, username, onBack, faction }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [chatChannel, setChatChannel] = useState('global');
  const [globalMessages, setGlobalMessages] = useState([]);
  const [factionMessages, setFactionMessages] = useState([]);
  const [crewMessages, setCrewMessages] = useState([]);
  const [dmMessages, setDmMessages] = useState([]);
  const [crew, setCrew] = useState(null);
  const [onlineCount, setOnlineCount] = useState(1);
  const socketRef = useRef(null);
  const playerFaction = faction || 'cmd';

  const FACTION_LABELS = { cmd: 'CMD', morelli: 'MORELLI', ddn: 'DDN' };
  const FACTION_COLORS = { cmd: '#C9A84C', morelli: '#8B1A1A', ddn: '#4A7AB5' };

  // Load crew info
  const loadCrew = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crew/my/${gameId}`);
      if (res.ok) {
        const d = await res.json();
        setCrew(d.crew);
      }
    } catch (e) {}
  }, [gameId]);

  // Load chat histories
  const loadGlobalChat = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/global?limit=50`);
      if (res.ok) {
        const d = await res.json();
        setGlobalMessages(d.messages || []);
      }
    } catch (e) {}
  }, []);

  const loadFactionChat = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/faction/${playerFaction}?limit=50`);
      if (res.ok) {
        const d = await res.json();
        setFactionMessages(d.messages || []);
      }
    } catch (e) {}
  }, [playerFaction]);

  const loadDMs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/direct/${gameId}`);
      if (res.ok) {
        const d = await res.json();
        setDmMessages(d.messages || []);
      }
    } catch (e) {}
  }, [gameId]);

  useEffect(() => {
    loadCrew();
    loadGlobalChat();
    loadFactionChat();
    loadDMs();
  }, [loadCrew, loadGlobalChat, loadFactionChat, loadDMs]);

  // WebSocket connection
  useEffect(() => {
    const s = getSocket();
    socketRef.current = s;

    s.emit('register', { game_id: gameId, username });

    s.on('message:global', (msg) => {
      if (msg.game_id === gameId) return; // skip own echo — already added optimistically
      setGlobalMessages(prev => [...prev.slice(-100), msg]);
    });

    s.on('message:faction', (msg) => {
      if (msg.faction === playerFaction) {
        if (msg.game_id === gameId) return; // skip own echo
        setFactionMessages(prev => [...prev.slice(-100), msg]);
      }
    });

    s.on('message:crew', (msg) => {
      if (msg.game_id === gameId) return; // skip own echo
      setCrewMessages(prev => [...prev.slice(-100), msg]);
    });

    s.on('message:direct', (msg) => {
      setDmMessages(prev => [...prev.slice(-100), msg]);
    });

    s.on('presence:count', (data) => {
      setOnlineCount(data.online || 1);
    });

    return () => {
      s.off('message:global');
      s.off('message:faction');
      s.off('message:crew');
      s.off('message:direct');
      s.off('presence:count');
    };
  }, [gameId, username, playerFaction]);

  const sendMessage = async (text, channel) => {
    const eventMap = { global: 'chat_global', faction: 'chat_faction', crew: 'chat_crew' };
    socketRef.current?.emit(eventMap[channel] || 'chat_global', { text, faction: playerFaction });

    const optimisticMsg = {
      game_id: gameId,
      username: username || 'You',
      text,
      timestamp: new Date().toISOString(),
      type: channel,
      faction: playerFaction,
    };
    if (channel === 'global') setGlobalMessages(prev => [...prev.slice(-100), optimisticMsg]);
    else if (channel === 'faction') setFactionMessages(prev => [...prev.slice(-100), optimisticMsg]);
    else if (channel === 'crew') setCrewMessages(prev => [...prev.slice(-100), optimisticMsg]);

    fetch(`${API}/api/chat/send`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, text, channel }),
    }).catch(() => {});
  };

  const channelMessages = {
    global: globalMessages,
    faction: factionMessages,
    crew: crewMessages,
    dm: dmMessages,
  };

  const tabs = [
    { id: 'chat', label: 'CHAT', icon: <MessageCircle size={12} /> },
    { id: 'crew', label: 'CREW', icon: <Users size={12} /> },
    { id: 'wars', label: 'WARS', icon: <Sword size={12} /> },
    { id: 'bounties', label: 'BOUNTIES', icon: <DollarSign size={12} /> },
  ];

  return (
    <div className="space-y-2" data-testid="communication-hub">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]" data-testid="comms-back">
          BACK
        </button>
        <div className="flex items-center gap-3">
          <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded border"
            style={{ color: FACTION_COLORS[playerFaction], borderColor: FACTION_COLORS[playerFaction] + '40' }}>
            {FACTION_LABELS[playerFaction]}
          </span>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse" />
            <span className="font-['Outfit'] text-[#4ADE80] text-[9px]">{onlineCount} online</span>
          </div>
        </div>
      </div>

      {/* Tab row */}
      <div className="flex gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-['Outfit'] text-[8px] tracking-wider transition-all border ${
              activeTab === t.id
                ? 'bg-[#C9A84C]/10 text-[#C9A84C] border-[#C9A84C]/30'
                : 'text-[#E8E0D0]/30 border-[#1A2535] hover:text-[#E8E0D0]/50'
            }`}
            data-testid={`tab-comms-${t.id}`}>
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Chat tab content */}
      {activeTab === 'chat' && (
        <div>
          {/* Channel selector — Global, Faction, Crew, DMs */}
          <div className="flex gap-1 mb-1 flex-wrap">
            <button onClick={() => setChatChannel('global')}
              className={`px-2 py-0.5 rounded font-['Outfit'] text-[7px] tracking-wider transition-colors ${
                chatChannel === 'global' ? 'bg-[#C9A84C]/10 text-[#C9A84C]' : 'text-[#E8E0D0]/20 hover:text-[#E8E0D0]/40'
              }`} data-testid="channel-global">
              GLOBAL
            </button>
            <button onClick={() => { setChatChannel('faction'); loadFactionChat(); }}
              className={`px-2 py-0.5 rounded font-['Outfit'] text-[7px] tracking-wider transition-colors ${
                chatChannel === 'faction' ? 'text-white' : 'text-[#E8E0D0]/20 hover:text-[#E8E0D0]/40'
              }`}
              style={chatChannel === 'faction' ? { background: FACTION_COLORS[playerFaction] + '20', color: FACTION_COLORS[playerFaction] } : {}}
              data-testid="channel-faction">
              {FACTION_LABELS[playerFaction]}
            </button>
            {crew && (
              <button onClick={() => setChatChannel('crew')}
                className={`px-2 py-0.5 rounded font-['Outfit'] text-[7px] tracking-wider transition-colors ${
                  chatChannel === 'crew' ? 'bg-[#4ADE80]/10 text-[#4ADE80]' : 'text-[#E8E0D0]/20 hover:text-[#E8E0D0]/40'
                }`} data-testid="channel-crew">
                CREW
              </button>
            )}
            <button onClick={() => { setChatChannel('dm'); loadDMs(); }}
              className={`px-2 py-0.5 rounded font-['Outfit'] text-[7px] tracking-wider transition-colors ${
                chatChannel === 'dm' ? 'bg-[#9B59B6]/10 text-[#9B59B6]' : 'text-[#E8E0D0]/20 hover:text-[#E8E0D0]/40'
              }`} data-testid="channel-dm">
              DMs {dmMessages.length > 0 && <span className="ml-1 text-[6px] opacity-50">({dmMessages.length})</span>}
            </button>
          </div>
          <ChatPanel
            gameId={gameId}
            username={username}
            crewId={crew?.crew_id}
            activeChannel={chatChannel}
            messages={channelMessages[chatChannel] || []}
            onSend={(text) => sendMessage(text, chatChannel)}
            factionColor={FACTION_COLORS[playerFaction]}
          />
        </div>
      )}

      {activeTab === 'crew' && (
        <CrewPanel gameId={gameId} crew={crew} onRefresh={loadCrew} />
      )}

      {activeTab === 'wars' && <WarPanel gameId={gameId} crew={crew} />}
      {activeTab === 'bounties' && <BountyBoard gameId={gameId} />}
    </div>
  );
};

export default CommunicationHub;
export { getSocket };
